import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Link, Navigate } from "react-router-dom";
import dashboardService from "../services/dashboardService";
import certificatesService from "../services/certificatesService";
import api from "../api";
import "./Dashboard.css";
import DeleteModal from "../components/DeleteModal.jsx/DeleteModal";
import clientDashboardStyles from "../assets/clientDashboard.module.css";
import { toast } from "react-toastify";

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const IconPlus = () => <i className="fa fa-plus" aria-hidden="true"></i>;
const IconEdit = () => <i className="fa fa-edit" aria-hidden="true"></i>;

// Pagination component
const Pagination = ({ currentPage, totalPages, onPageChange, itemsPerPage, onItemsPerPageChange, totalItems }) => {
  const pageNumbers = [];
  const maxPagesToShow = 5;

  let startPage = Math.max(0, currentPage - Math.floor(maxPagesToShow / 2));
  let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);

  if (endPage - startPage < maxPagesToShow - 1) {
    startPage = Math.max(0, endPage - maxPagesToShow + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', fontSize: '14px' }}>
      <div>
        Showing {currentPage * itemsPerPage + 1} to {Math.min((currentPage + 1) * itemsPerPage, totalItems)} of {totalItems} entries
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span>Show</span>
        <select
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          style={{ padding: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
        <span>entries</span>
      </div>
      <div style={{ display: 'flex', gap: '5px' }}>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 0}
          style={{
            padding: '5px 10px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            background: currentPage === 0 ? '#f5f5f5' : 'white',
            cursor: currentPage === 0 ? 'not-allowed' : 'pointer'
          }}
        >
          Previous
        </button>
        {pageNumbers.map(pageNum => (
          <button
            key={pageNum}
            onClick={() => onPageChange(pageNum)}
            style={{
              padding: '5px 10px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              background: pageNum === currentPage ? '#007bff' : 'white',
              color: pageNum === currentPage ? 'white' : 'black',
              cursor: 'pointer'
            }}
          >
            {pageNum + 1}
          </button>
        ))}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages - 1}
          style={{
            padding: '5px 10px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            background: currentPage >= totalPages - 1 ? '#f5f5f5' : 'white',
            cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer'
          }}
        >
          Next
        </button>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [certificates, setCertificates] = useState([]);
  const [certificatesLoading, setCertificatesLoading] = useState(false);
  const [certificateFilters, setCertificateFilters] = useState({
    certificate_type: "all",
    status: "all",
    property_type: "active",
  });
  
  // Sorting states
  const [propertiesSortConfig, setPropertiesSortConfig] = useState({ key: null, direction: 'asc' });
  const [blocksSortConfig, setBlocksSortConfig] = useState({ key: null, direction: 'asc' });
  const [certificatesSortConfig, setCertificatesSortConfig] = useState({ key: null, direction: 'asc' });

  // Search states
  const [propertiesSearch, setPropertiesSearch] = useState('');

  // Pagination states
  const [propertiesPage, setPropertiesPage] = useState(0);
  const [propertiesPerPage, setPropertiesPerPage] = useState(10);
  const [blocksPage, setBlocksPage] = useState(0);
  const [blocksPerPage, setBlocksPerPage] = useState(10);
  const [certificatesPage, setCertificatesPage] = useState(0);
  const [certificatesPerPage, setCertificatesPerPage] = useState(10);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await dashboardService.getDashboardData();
        setStats(data);
      } catch (err) {
        setError(err.message || "Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleLogout = () => {
    logout();
  };

  const handleCertificateSearch = async (e) => {
    e.preventDefault();
    setCertificatesLoading(true);
    try {
      // Convert string filter values to numbers expected by backend
      // property_type: "all" → 0, "active" → 1
      const property_type = certificateFilters.property_type === "all" ? 0 : 1;

      // cert_status: "all" → 0, "active" → 1, "expired" → 2, "due_to_expire" → 3, "archived" → 4
      const statusMap = { all: 0, active: 1, expired: 2, due_to_expire: 3, archived: 4 };
      const cert_status = statusMap[certificateFilters.status] || 0;

      // type_id: certificate type string to numeric ID
      const typeMap = {
        all: 0, gas_safe: 6, nic: 7, asbestos: 8, fire_risk: 9, legionella: 10,
        fire_alarm: 30, emergency_lighting: 31, fire_extinguisher: 32, pat_testing: 33, "240v": 29
      };
      const type_id = typeMap[certificateFilters.certificate_type] || 0;

      // Include length: 10000 to fetch all certificates (matching Client View behavior)
      const response = await certificatesService.getClientCertificates({
        property_type,
        cert_status,
        type_id,
        length: 10000,
        start: 0,
      });
      if (response.success) {
        setCertificates(response.data || []);
      } else {
        toast.error("Failed to load certificates");
      }
    } catch (error) {
      console.error("Error loading certificates:", error);
      toast.error("Failed to load certificates");
    } finally {
      setCertificatesLoading(false);
    }
  };

  const handleCertificateFilterChange = (filterName, value) => {
    setCertificateFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const handleCertificateDownload = async (documentId) => {
    if (!documentId) {
      toast.error("Certificate document ID is missing");
      return;
    }

    try {
      // Use the same endpoint as Client View: /certificates/:documentId/download
      const response = await api.get(`/certificates/${documentId}/download`);
      const data = response.data;

      if (data.success && data.downloadUrl) {
        // Open the presigned URL in a new tab
        window.open(data.downloadUrl, '_blank');
        toast.success("Certificate download started");
      } else {
        toast.error('Unable to download certificate: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error("Error downloading certificate:", error);
      toast.error(`Failed to download certificate: ${error.message}`);
    }
  };

  // Sorting functions
  const handleSort = (key, sortConfig, setSortConfig) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortData = (data, sortConfig) => {
    // Ensure data is always an array
    if (!Array.isArray(data)) return [];
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle nested properties
      if (sortConfig.key === 'name' && a.name) aValue = a.name;
      if (sortConfig.key === 'name' && b.name) bValue = b.name;

      // Handle date fields with validation
      if (sortConfig.key === 'date_taken_on' || sortConfig.key === 'completed' || sortConfig.key === 'expiration') {
        // Only convert to Date if the value is valid
        if (aValue) {
          const aDate = new Date(aValue);
          aValue = isNaN(aDate.getTime()) ? null : aDate;
        } else {
          aValue = null;
        }

        if (bValue) {
          const bDate = new Date(bValue);
          bValue = isNaN(bDate.getTime()) ? null : bDate;
        } else {
          bValue = null;
        }

        // Handle null values - push them to the end
        if (aValue === null && bValue === null) return 0;
        if (aValue === null) return 1;
        if (bValue === null) return -1;
      }

      // Handle numeric fields
      if (sortConfig.key === 'properties_count') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Filtered + sorted data
  const filteredProperties = useMemo(() => {
    const properties = stats?.properties || [];
    if (!propertiesSearch.trim()) return properties;
    const term = propertiesSearch.toLowerCase();
    return properties.filter(p =>
      (p.name || '').toLowerCase().includes(term) ||
      (p.property_id || '').toString().toLowerCase().includes(term) ||
      (p.postcode || '').toLowerCase().includes(term) ||
      (p.status || '').toLowerCase().includes(term) ||
      (p.date_taken_on || '').toLowerCase().includes(term)
    );
  }, [stats?.properties, propertiesSearch]);

  const sortedProperties = useMemo(() =>
    sortData(filteredProperties, propertiesSortConfig),
    [filteredProperties, propertiesSortConfig]
  );

  const sortedBlocks = useMemo(() =>
    sortData(stats?.blocks || [], blocksSortConfig),
    [stats?.blocks, blocksSortConfig]
  );

  const sortedCertificates = useMemo(() =>
    sortData(certificates || [], certificatesSortConfig),
    [certificates, certificatesSortConfig]
  );

  // Paginated data
  const paginatedProperties = useMemo(() => {
    const start = propertiesPage * propertiesPerPage;
    return sortedProperties.slice(start, start + propertiesPerPage);
  }, [sortedProperties, propertiesPage, propertiesPerPage]);

  const paginatedBlocks = useMemo(() => {
    const start = blocksPage * blocksPerPage;
    return sortedBlocks.slice(start, start + blocksPerPage);
  }, [sortedBlocks, blocksPage, blocksPerPage]);

  const paginatedCertificates = useMemo(() => {
    const start = certificatesPage * certificatesPerPage;
    return sortedCertificates.slice(start, start + certificatesPerPage);
  }, [sortedCertificates, certificatesPage, certificatesPerPage]);

  // Pagination helpers
  const totalPropertiesPages = Math.ceil(sortedProperties.length / propertiesPerPage);
  const totalBlocksPages = Math.ceil(sortedBlocks.length / blocksPerPage);
  const totalCertificatesPages = Math.ceil(sortedCertificates.length / certificatesPerPage);

  const canCreateAdmins =
    user && ["SYSTEM-ADMINISTRATOR", "SENIOR-MANAGEMENT"].includes(user.role);

  // Check if user is a GUARDIAN
  const isGuardian = user && user.role === "GUARDIAN";

  // Check if user is a CLIENT
  const isClient = user && user.role === "CLIENT";

  // Check if user is SALES
  const isSales = user && user.role === "SALES";

  // Check if user is SALES WITH GUARDIANS
  const isSalesWithGuardians = user && user.role === "SALES WITH GUARDIANS";

  // Check if user is GUARDIAN-MANAGER (all variants)
  const isGuardianManager = user && (
    user.role === "GUARDIAN-MANAGER" ||
    user.role === "GUARDIAN-MANAGER-WITH-MAINTENANCE" ||
    user.role === "GUARDIAN-MAINTENANCE-CLIENT" ||
    user.role === "GUARDIAN-MANAGER-WITH-PROPERTIES-EDIT"
  );

  // Redirect guardians to the new guardian dashboard
  if (isGuardian) {
    return <Navigate to="/guardian-dashboard" replace />;
  }

  // Redirect SALES to clients page (matching Laravel salesDashboard behavior)
  if (isSales) {
    return <Navigate to="/clients" replace />;
  }

  // Redirect SALES WITH GUARDIANS to their specific dashboard
  if (isSalesWithGuardians) {
    return <Navigate to="/sales-with-guardians-dashboard" replace />;
  }

  // Redirect GUARDIAN-MANAGER (all variants) to their specific dashboard
  if (isGuardianManager) {
    return <Navigate to="/guardian-manager-dashboard" replace />;
  }

  // Render client dashboard (replicating Laravel client dashboard exactly)
  if (isClient) {
    return (
      <div className={clientDashboardStyles.page}>
        <DeleteModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={() => alert()}
        />
        <div className={clientDashboardStyles.container}>
          {/* Breadcrumb */}
          <div className={clientDashboardStyles.breadcrumb}>
            <div className={clientDashboardStyles.dashboardTag}>Dashboard</div>
          </div>

          {/* Savings & VIP Section */}
          <div className={clientDashboardStyles.savingsSection}>
            <div className={clientDashboardStyles.savingsTitle}>
              Property savings & voids into profit to date
            </div>
            <div className={clientDashboardStyles.savingsRow}>
              <div
                className={`${clientDashboardStyles.savingsBox} ${clientDashboardStyles.savingsBoxRed}`}
              >
                <div className={clientDashboardStyles.savingsBoxIcon}>
                  <img src="/images/peppa-red.png" alt="Savings" />
                </div>
                <div className={clientDashboardStyles.savingsBoxContent}>
                  <p className={clientDashboardStyles.cash}>
                    +£
                    {stats?.savingsMade
                      ? Number(stats.savingsMade).toLocaleString("en-GB", {
                          minimumFractionDigits: 2,
                        })
                      : "0.00"}
                  </p>
                  <p className={clientDashboardStyles.savingInformation}>
                    SAVINGS MADE BY USING GLOBAL GUARDIANS
                  </p>
                </div>
              </div>
              <div
                className={`${clientDashboardStyles.savingsBox} ${clientDashboardStyles.savingsBoxMint}`}
              >
                <div className={clientDashboardStyles.savingsBoxIcon}>
                  <img src="/images/hand-mint.png" alt="VIP" />
                </div>
                <div className={clientDashboardStyles.savingsBoxContent}>
                  <p className={clientDashboardStyles.cash}>
                    +£
                    {stats?.vipAmount
                      ? Number(stats.vipAmount).toLocaleString("en-GB", {
                          minimumFractionDigits: 2,
                        })
                      : "0.00"}
                  </p>
                  <p className={clientDashboardStyles.savingInformation}>
                    INCOME GENERATED THROUGH VOIDS INTO PROFIT
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Properties Section */}
          <div className={clientDashboardStyles.propertiesSection}>
            <div className={clientDashboardStyles.sectionTitle}>Properties</div>
            <div className={clientDashboardStyles.dataTableWrapper}>
              {stats?.propertiesNumber > 0 ? (
                <>
                <div className={clientDashboardStyles.tableToolbar}>
                  <div className={clientDashboardStyles.toolbarLeft}>
                    <span>Show</span>
                    <select
                      value={propertiesPerPage}
                      onChange={(e) => {
                        setPropertiesPerPage(Number(e.target.value));
                        setPropertiesPage(0);
                      }}
                      className={clientDashboardStyles.toolbarSelect}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span>entries</span>
                  </div>
                  <div className={clientDashboardStyles.toolbarRight}>
                    <label>Search:</label>
                    <input
                      type="text"
                      value={propertiesSearch}
                      onChange={(e) => {
                        setPropertiesSearch(e.target.value);
                        setPropertiesPage(0);
                      }}
                      className={clientDashboardStyles.toolbarSearch}
                    />
                  </div>
                </div>
                <table className={clientDashboardStyles.table}>
                  <thead>
                    <tr>
                      <th 
                        onClick={() => handleSort('name', propertiesSortConfig, setPropertiesSortConfig)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Property Name
                        <SortIcon 
                          direction={propertiesSortConfig.key === 'name' ? propertiesSortConfig.direction : null}
                          isActive={propertiesSortConfig.key === 'name'}
                        />
                      </th>
                      <th 
                        onClick={() => handleSort('property_id', propertiesSortConfig, setPropertiesSortConfig)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Property ID
                        <SortIcon 
                          direction={propertiesSortConfig.key === 'property_id' ? propertiesSortConfig.direction : null}
                          isActive={propertiesSortConfig.key === 'property_id'}
                        />
                      </th>
                      <th 
                        onClick={() => handleSort('postcode', propertiesSortConfig, setPropertiesSortConfig)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Postcode
                        <SortIcon 
                          direction={propertiesSortConfig.key === 'postcode' ? propertiesSortConfig.direction : null}
                          isActive={propertiesSortConfig.key === 'postcode'}
                        />
                      </th>
                      <th 
                        onClick={() => handleSort('status', propertiesSortConfig, setPropertiesSortConfig)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Status
                        <SortIcon 
                          direction={propertiesSortConfig.key === 'status' ? propertiesSortConfig.direction : null}
                          isActive={propertiesSortConfig.key === 'status'}
                        />
                      </th>
                      <th 
                        onClick={() => handleSort('date_taken_on', propertiesSortConfig, setPropertiesSortConfig)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Taken On
                        <SortIcon 
                          direction={propertiesSortConfig.key === 'date_taken_on' ? propertiesSortConfig.direction : null}
                          isActive={propertiesSortConfig.key === 'date_taken_on'}
                        />
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedProperties?.map((property) => (
                      <tr key={property.id}>
                        <td>
                          <Link
                            to={`/properties/${property.id}`}
                            className="hidden-print-link"
                          >
                            {property.name}
                          </Link>
                        </td>
                        <td>{property.property_id}</td>
                        <td>{property.postcode}</td>
                        <td>{property.status}</td>
                        <td>{property.date_taken_on}</td>
                        <td>
                          <Link
                            to={`/properties/${property.id}`}
                            className="btn btn-sm btn-default"
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', fontSize: '14px' }}>
                  <div>
                    Showing {sortedProperties.length === 0 ? 0 : propertiesPage * propertiesPerPage + 1} to {Math.min((propertiesPage + 1) * propertiesPerPage, sortedProperties.length)} of {sortedProperties.length} entries
                    {propertiesSearch && stats?.properties && sortedProperties.length !== stats.properties.length && (
                      <span> (filtered from {stats.properties.length} total entries)</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button
                      onClick={() => setPropertiesPage(propertiesPage - 1)}
                      disabled={propertiesPage === 0}
                      style={{
                        padding: '5px 10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        background: propertiesPage === 0 ? '#f5f5f5' : 'white',
                        cursor: propertiesPage === 0 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.min(totalPropertiesPages, 5) }, (_, i) => {
                      let startPage = Math.max(0, propertiesPage - 2);
                      let endPage = Math.min(totalPropertiesPages - 1, startPage + 4);
                      if (endPage - startPage < 4) startPage = Math.max(0, endPage - 4);
                      const pageNum = startPage + i;
                      if (pageNum > endPage) return null;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPropertiesPage(pageNum)}
                          style={{
                            padding: '5px 10px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            background: pageNum === propertiesPage ? '#007bff' : 'white',
                            color: pageNum === propertiesPage ? 'white' : 'black',
                            cursor: 'pointer'
                          }}
                        >
                          {pageNum + 1}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setPropertiesPage(propertiesPage + 1)}
                      disabled={propertiesPage >= totalPropertiesPages - 1}
                      style={{
                        padding: '5px 10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        background: propertiesPage >= totalPropertiesPages - 1 ? '#f5f5f5' : 'white',
                        cursor: propertiesPage >= totalPropertiesPages - 1 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
                </>
              ) : (
                <div className={clientDashboardStyles.noDataMessage}>
                  No property!
                </div>
              )}
            </div>
          </div>

          {/* Global Guardians Services Section */}
          <div className={clientDashboardStyles.servicesSection}>
            <div className={clientDashboardStyles.servicesTitle}>
              Global Guardians services
            </div>
            <div className={clientDashboardStyles.servicesGrid}>
              <a
                href="/client-knowledge/property-guardian-solution"
                className={clientDashboardStyles.serviceBox}
              >
                <img
                  src="/images/services/property_guardian_solution_new.jpg"
                  alt="Property Guardian Solutions"
                  className={clientDashboardStyles.serviceImage}
                />
                <div className={clientDashboardStyles.serviceText}>
                  Property Guardian Solutions
                </div>
              </a>
              <a
                href="/client-knowledge/luxury-house-sitting"
                className={clientDashboardStyles.serviceBox}
              >
                <img
                  src="/images/services/high_end_house_sitters_new.jpg"
                  alt="High End House Sitters"
                  className={clientDashboardStyles.serviceImage}
                />
                <div className={clientDashboardStyles.serviceText}>
                  High End House Sitters
                </div>
              </a>
              <a
                href="/client-knowledge/safety-management"
                className={clientDashboardStyles.serviceBox}
              >
                <img
                  src="/images/services/safety_management_new.jpg"
                  alt="Safety Management"
                  className={clientDashboardStyles.serviceImage}
                />
                <div className={clientDashboardStyles.serviceText}>
                  Safety
                  <br />
                  Management
                </div>
              </a>
              <a
                href="/client-knowledge/building-management"
                className={clientDashboardStyles.serviceBox}
              >
                <img
                  src="/images/services/building_management_new.jpg"
                  alt="Building Management"
                  className={clientDashboardStyles.serviceImage}
                />
                <div className={clientDashboardStyles.serviceText}>
                  Building
                  <br />
                  Management
                </div>
              </a>
              <a
                href="/client-knowledge/improve-your-void"
                className={clientDashboardStyles.serviceBox}
              >
                <img
                  src="/images/services/improve_your_voids_new.jpg"
                  alt="Improve Your Void"
                  className={clientDashboardStyles.serviceImage}
                />
                <div className={clientDashboardStyles.serviceText}>
                  Improve Your
                  <br />
                  Void
                </div>
              </a>
              <a
                href="/client-knowledge/life-enhancing-chances"
                className={clientDashboardStyles.serviceBox}
              >
                <img
                  src="/images/services/life_enhancing_chances_new.jpg"
                  alt="Life Enhancing Chances"
                  className={clientDashboardStyles.serviceImage}
                />
                <div className={clientDashboardStyles.serviceText}>
                  Life Enhancing Chances
                </div>
              </a>
              <a
                href="/client-knowledge/property-guardian-insurance"
                className={clientDashboardStyles.serviceBox}
              >
                <img
                  src="/images/services/property_guardian_insurance_new.jpg"
                  alt="Property Guardian Insurance"
                  className={clientDashboardStyles.serviceImage}
                />
                <div className={clientDashboardStyles.serviceText}>
                  Property Guardian Insurance
                </div>
              </a>
              <a
                href="/client-knowledge/void-into-profit"
                className={clientDashboardStyles.serviceBox}
              >
                <img
                  src="/images/services/voids_into_profit_new.jpg"
                  alt="Void Into Profit"
                  className={clientDashboardStyles.serviceImage}
                />
                <div className={clientDashboardStyles.serviceText}>
                  Void Into
                  <br />
                  Profit
                </div>
              </a>
              <a
                href="/client-knowledge/proptech-software-solutions"
                className={clientDashboardStyles.serviceBox}
              >
                <img
                  src="/images/services/empty_building_rates_reduction_new.jpg"
                  alt="Proptech Software Solutions"
                  className={clientDashboardStyles.serviceImage}
                />
                <div className={clientDashboardStyles.serviceText}>
                  Proptech Software Solutions
                </div>
              </a>
            </div>
          </div>

          {/* Blocks Section (if client has blocks) */}
          {stats?.blocks && stats.blocks.length > 0 && (
            <div className={clientDashboardStyles.blocksSection}>
              <div className={clientDashboardStyles.sectionTitle}>Blocks</div>
              <div className={clientDashboardStyles.dataTableWrapper}>
                <>
                <table className={clientDashboardStyles.table}>
                  <thead>
                    <tr>
                      <th
                        onClick={() => handleSort('name', blocksSortConfig, setBlocksSortConfig)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Name
                        <SortIcon
                          direction={blocksSortConfig.key === 'name' ? blocksSortConfig.direction : null}
                          isActive={blocksSortConfig.key === 'name'}
                        />
                      </th>
                      <th
                        onClick={() => handleSort('properties_count', blocksSortConfig, setBlocksSortConfig)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        No. properties
                        <SortIcon
                          direction={blocksSortConfig.key === 'properties_count' ? blocksSortConfig.direction : null}
                          isActive={blocksSortConfig.key === 'properties_count'}
                        />
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedBlocks.map((block) => (
                      <tr key={block.id}>
                        <td>{block.name}</td>
                        <td>{block.properties_count}</td>
                        <td>
                          <Link
                            to={`/blocks/${block.id}`}
                            className="btn btn-sm btn-default"
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Pagination
                  currentPage={blocksPage}
                  totalPages={totalBlocksPages}
                  onPageChange={setBlocksPage}
                  itemsPerPage={blocksPerPage}
                  onItemsPerPageChange={(newSize) => {
                    setBlocksPerPage(newSize);
                    setBlocksPage(0);
                  }}
                  totalItems={sortedBlocks.length}
                />
                </>
              </div>
            </div>
          )}

          {/* Reports Section */}
          {stats?.reports && stats.reports.length > 0 && (
            <div className={clientDashboardStyles.reportsSection}>
              <div className={clientDashboardStyles.sectionTitle}>Reports</div>
              <div className={clientDashboardStyles.reportsGrid}>
                {stats.reports.map((report) => (
                  <Link
                    key={report.id}
                    to={`/monthly_inspections/${stats.clientId}/${report.id}`}
                    className={clientDashboardStyles.reportCard}
                  >
                    <div className={clientDashboardStyles.reportHeader}>
                      <div style={{ fontSize: "40px", marginRight: "15px" }}>
                        📄
                      </div>
                      <div>
                        <p className={clientDashboardStyles.reportTitle}>
                          {report.months} ({report.year})
                        </p>
                        <p className={clientDashboardStyles.reportDate}>
                          Updated:{" "}
                          {new Date(report.updated_at).toLocaleDateString(
                            "en-GB"
                          )}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Certificates Section */}
          <div className={clientDashboardStyles.certificatesSection}>
            <div className={clientDashboardStyles.sectionTitle}>
              Certificates
            </div>
            <div className={clientDashboardStyles.certificatesForm}>
              <form onSubmit={handleCertificateSearch} className="form-inline">
                <div className={clientDashboardStyles.formRow}>
                  <div className={clientDashboardStyles.filterGroup}>
                    <label className={clientDashboardStyles.formLabel}>
                      Certificate
                    </label>
                    <div className={clientDashboardStyles.radioGroup}>
                      <label className={clientDashboardStyles.radioLabel}>
                        <input
                          type="radio"
                          name="certificate_type"
                          value="all"
                          checked={
                            certificateFilters.certificate_type === "all"
                          }
                          onChange={(e) =>
                            handleCertificateFilterChange(
                              "certificate_type",
                              e.target.value
                            )
                          }
                        />
                        <span>All</span>
                      </label>
                      <label className={clientDashboardStyles.radioLabel}>
                        <input
                          type="radio"
                          name="certificate_type"
                          value="gas_safe"
                          checked={
                            certificateFilters.certificate_type === "gas_safe"
                          }
                          onChange={(e) =>
                            handleCertificateFilterChange(
                              "certificate_type",
                              e.target.value
                            )
                          }
                        />
                        <span>Gas Safe Certificate</span>
                      </label>
                      <label className={clientDashboardStyles.radioLabel}>
                        <input
                          type="radio"
                          name="certificate_type"
                          value="nic"
                          checked={
                            certificateFilters.certificate_type === "nic"
                          }
                          onChange={(e) =>
                            handleCertificateFilterChange(
                              "certificate_type",
                              e.target.value
                            )
                          }
                        />
                        <span>NIC Certificate</span>
                      </label>
                      <label className={clientDashboardStyles.radioLabel}>
                        <input
                          type="radio"
                          name="certificate_type"
                          value="asbestos"
                          checked={
                            certificateFilters.certificate_type === "asbestos"
                          }
                          onChange={(e) =>
                            handleCertificateFilterChange(
                              "certificate_type",
                              e.target.value
                            )
                          }
                        />
                        <span>Asbestos Register</span>
                      </label>
                      <label className={clientDashboardStyles.radioLabel}>
                        <input
                          type="radio"
                          name="certificate_type"
                          value="fire_risk"
                          checked={
                            certificateFilters.certificate_type === "fire_risk"
                          }
                          onChange={(e) =>
                            handleCertificateFilterChange(
                              "certificate_type",
                              e.target.value
                            )
                          }
                        />
                        <span>Fire Risk Assessment</span>
                      </label>
                      <label className={clientDashboardStyles.radioLabel}>
                        <input
                          type="radio"
                          name="certificate_type"
                          value="legionella"
                          checked={
                            certificateFilters.certificate_type === "legionella"
                          }
                          onChange={(e) =>
                            handleCertificateFilterChange(
                              "certificate_type",
                              e.target.value
                            )
                          }
                        />
                        <span>Legionella</span>
                      </label>
                      <label className={clientDashboardStyles.radioLabel}>
                        <input
                          type="radio"
                          name="certificate_type"
                          value="fire_alarm"
                          checked={
                            certificateFilters.certificate_type === "fire_alarm"
                          }
                          onChange={(e) =>
                            handleCertificateFilterChange(
                              "certificate_type",
                              e.target.value
                            )
                          }
                        />
                        <span>Fire Alarm</span>
                      </label>
                      <label className={clientDashboardStyles.radioLabel}>
                        <input
                          type="radio"
                          name="certificate_type"
                          value="emergency_lighting"
                          checked={
                            certificateFilters.certificate_type ===
                            "emergency_lighting"
                          }
                          onChange={(e) =>
                            handleCertificateFilterChange(
                              "certificate_type",
                              e.target.value
                            )
                          }
                        />
                        <span>Emergency Lighting</span>
                      </label>
                      <label className={clientDashboardStyles.radioLabel}>
                        <input
                          type="radio"
                          name="certificate_type"
                          value="fire_extinguisher"
                          checked={
                            certificateFilters.certificate_type ===
                            "fire_extinguisher"
                          }
                          onChange={(e) =>
                            handleCertificateFilterChange(
                              "certificate_type",
                              e.target.value
                            )
                          }
                        />
                        <span>Fire Extinguisher Certificate</span>
                      </label>
                      <label className={clientDashboardStyles.radioLabel}>
                        <input
                          type="radio"
                          name="certificate_type"
                          value="pat_testing"
                          checked={
                            certificateFilters.certificate_type ===
                            "pat_testing"
                          }
                          onChange={(e) =>
                            handleCertificateFilterChange(
                              "certificate_type",
                              e.target.value
                            )
                          }
                        />
                        <span>PAT Testing</span>
                      </label>
                      <label className={clientDashboardStyles.radioLabel}>
                        <input
                          type="radio"
                          name="certificate_type"
                          value="240v"
                          checked={
                            certificateFilters.certificate_type === "240v"
                          }
                          onChange={(e) =>
                            handleCertificateFilterChange(
                              "certificate_type",
                              e.target.value
                            )
                          }
                        />
                        <span>240V Certificate</span>
                      </label>
                    </div>
                  </div>

                  <div className={clientDashboardStyles.filterGroup}>
                    <label className={clientDashboardStyles.formLabel}>
                      Status
                    </label>
                    <div className={clientDashboardStyles.radioGroup}>
                      <label className={clientDashboardStyles.radioLabel}>
                        <input
                          type="radio"
                          name="status"
                          value="all"
                          checked={certificateFilters.status === "all"}
                          onChange={(e) =>
                            handleCertificateFilterChange(
                              "status",
                              e.target.value
                            )
                          }
                        />
                        <span>All</span>
                      </label>
                      <label className={clientDashboardStyles.radioLabel}>
                        <input
                          type="radio"
                          name="status"
                          value="active"
                          checked={certificateFilters.status === "active"}
                          onChange={(e) =>
                            handleCertificateFilterChange(
                              "status",
                              e.target.value
                            )
                          }
                        />
                        <span>Active</span>
                      </label>
                      <label className={clientDashboardStyles.radioLabel}>
                        <input
                          type="radio"
                          name="status"
                          value="archived"
                          checked={certificateFilters.status === "archived"}
                          onChange={(e) =>
                            handleCertificateFilterChange(
                              "status",
                              e.target.value
                            )
                          }
                        />
                        <span>Archived</span>
                      </label>
                      <label className={clientDashboardStyles.radioLabel}>
                        <input
                          type="radio"
                          name="status"
                          value="due_to_expire"
                          checked={
                            certificateFilters.status === "due_to_expire"
                          }
                          onChange={(e) =>
                            handleCertificateFilterChange(
                              "status",
                              e.target.value
                            )
                          }
                        />
                        <span>Due to expire</span>
                      </label>
                      <label className={clientDashboardStyles.radioLabel}>
                        <input
                          type="radio"
                          name="status"
                          value="expired"
                          checked={certificateFilters.status === "expired"}
                          onChange={(e) =>
                            handleCertificateFilterChange(
                              "status",
                              e.target.value
                            )
                          }
                        />
                        <span>Expired</span>
                      </label>
                    </div>
                  </div>

                  <div className={clientDashboardStyles.filterGroup}>
                    <label className={clientDashboardStyles.formLabel}>
                      Property Type
                    </label>
                    <div className={clientDashboardStyles.radioGroup}>
                      <label className={clientDashboardStyles.radioLabel}>
                        <input
                          type="radio"
                          name="property_type"
                          value="all"
                          checked={certificateFilters.property_type === "all"}
                          onChange={(e) =>
                            handleCertificateFilterChange(
                              "property_type",
                              e.target.value
                            )
                          }
                        />
                        <span>All</span>
                      </label>
                      <label className={clientDashboardStyles.radioLabel}>
                        <input
                          type="radio"
                          name="property_type"
                          value="active"
                          checked={
                            certificateFilters.property_type === "active"
                          }
                          onChange={(e) =>
                            handleCertificateFilterChange(
                              "property_type",
                              e.target.value
                            )
                          }
                        />
                        <span>Active only</span>
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className={clientDashboardStyles.searchButton}
                    disabled={certificatesLoading}
                  >
                    {certificatesLoading ? "Searching..." : "Search"}
                  </button>
                </div>
              </form>
            </div>
            <div className={clientDashboardStyles.dataTableWrapper}>
              {certificatesLoading ? (
                <div className={clientDashboardStyles.loadingMessage}>
                  Loading certificates...
                </div>
              ) : certificates.length > 0 ? (
                <>
                <table className={clientDashboardStyles.table}>
                  <thead>
                    <tr>
                      <th
                        onClick={() => handleSort('type', certificatesSortConfig, setCertificatesSortConfig)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Type
                        <SortIcon
                          direction={certificatesSortConfig.key === 'type' ? certificatesSortConfig.direction : null}
                          isActive={certificatesSortConfig.key === 'type'}
                        />
                      </th>
                      <th
                        onClick={() => handleSort('completed', certificatesSortConfig, setCertificatesSortConfig)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Completed
                        <SortIcon
                          direction={certificatesSortConfig.key === 'completed' ? certificatesSortConfig.direction : null}
                          isActive={certificatesSortConfig.key === 'completed'}
                        />
                      </th>
                      <th
                        onClick={() => handleSort('expiration', certificatesSortConfig, setCertificatesSortConfig)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Expiration
                        <SortIcon
                          direction={certificatesSortConfig.key === 'expiration' ? certificatesSortConfig.direction : null}
                          isActive={certificatesSortConfig.key === 'expiration'}
                        />
                      </th>
                      <th
                        onClick={() => handleSort('contractor_appointment', certificatesSortConfig, setCertificatesSortConfig)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Contractor Appointment
                        <SortIcon
                          direction={certificatesSortConfig.key === 'contractor_appointment' ? certificatesSortConfig.direction : null}
                          isActive={certificatesSortConfig.key === 'contractor_appointment'}
                        />
                      </th>
                      <th
                        onClick={() => handleSort('propertyName', certificatesSortConfig, setCertificatesSortConfig)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Property
                        <SortIcon
                          direction={certificatesSortConfig.key === 'propertyName' ? certificatesSortConfig.direction : null}
                          isActive={certificatesSortConfig.key === 'propertyName'}
                        />
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCertificates.map((certificate) => (
                      <tr key={certificate.id}>
                        <td>{certificate.type}</td>
                        <td>{certificate.completed}</td>
                        <td>{certificate.expiration}</td>
                        <td>{certificate.contractor_appointment}</td>
                        <td>
                          <Link
                            to={`/properties/${certificate.propertyId}`}
                            style={{ fontWeight: 700 }}
                          >
                            {certificate.propertyName}
                          </Link>
                        </td>
                        <td>
                          <button
                            onClick={() =>
                              handleCertificateDownload(certificate.documentId)
                            }
                            className="btn btn-sm btn-warning"
                          >
                            Download
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Pagination
                  currentPage={certificatesPage}
                  totalPages={totalCertificatesPages}
                  onPageChange={setCertificatesPage}
                  itemsPerPage={certificatesPerPage}
                  onItemsPerPageChange={(newSize) => {
                    setCertificatesPerPage(newSize);
                    setCertificatesPage(0);
                  }}
                  totalItems={sortedCertificates.length}
                />
                </>
              ) : (
                <div className={clientDashboardStyles.noDataMessage}>
                  No certificates found. Use the search form above to find
                  certificates.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render admin dashboard (existing code for SYSTEM-ADMINISTRATOR and other roles)
  return (
    <div className="dashboard-layout">
      <DeleteModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={() => alert()}
      />
      <main className="dashboard-content">
        <div className="container">
          <div className="dashboard-header">
            <h1>Dashboard</h1>
          </div>
          <div className="row">
            <div className="col-md-4">
              <div className="dashbox">
                <div className="dashbox-heading">
                  <h3>Users</h3>
                </div>
                <div className="dashbox-body">
                  <Link
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      textTransform: "capitalize",
                    }}
                    to="/users"
                    className="btn btn-large btn-default btn-mint col-md-6"
                  >
                    List users
                  </Link>
                  <Link
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      textTransform: "capitalize",
                    }}
                    to="/users/create"
                    className="btn btn-large btn-default btn-mint col-md-6"
                  >
                    <IconPlus /> Add new user
                  </Link>
                </div>
                <div className="dashbox-footer">
                  <div className="dashbox_footer_labels">
                    <span className="dashbox-footer-label">
                      Number of users:
                    </span>
                    <span className="dashbox-badge">{stats?.users ?? "-"}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="dashbox">
                <div className="dashbox-heading">
                  <h3>Clients</h3>
                </div>
                <div className="dashbox-body">
                  <Link
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      textTransform: "capitalize",
                    }}
                    to="/clients"
                    className="btn btn-large btn-default btn-mint col-md-6"
                  >
                    List clients
                  </Link>
                  <Link
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      textTransform: "capitalize",
                    }}
                    to="/clients/create"
                    className="btn btn-large btn-default btn-mint col-md-6"
                  >
                    <IconPlus /> Add new client
                  </Link>
                </div>
                <div className="dashbox-footer">
                  <div className="dashbox_footer_labels">
                    <span className="dashbox-footer-label">All clients:</span>
                    <span className="dashbox-badge">
                      {stats?.clients ?? "-"}
                    </span>
                  </div>
                  <br />
                  <div className="dashbox_footer_labels">
                    <span className="dashbox-footer-label">Live clients:</span>
                    <span className="dashbox-badge">
                      {stats?.clientsLive ?? "-"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="dashbox">
                <div className="dashbox-heading">
                  <h3>Properties</h3>
                </div>
                <div className="dashbox-body">
                  <Link
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      textTransform: "capitalize",
                    }}
                    to="/properties"
                    className="btn btn-large btn-default btn-mint col-md-6"
                  >
                    List properties
                  </Link>
                  <Link
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      textTransform: "capitalize",
                    }}
                    to="/properties/create"
                    className="btn btn-large btn-default btn-mint col-md-6"
                  >
                    <IconPlus /> Add new property
                  </Link>
                </div>
                <div className="dashbox-footer">
                  <div className="dashbox_footer_labels">
                    <span className="dashbox-footer-label">
                      All properties:
                    </span>
                    <span className="dashbox-badge">
                      {stats?.properties ?? "-"}
                    </span>
                  </div>
                  <br />
                  <div className="dashbox_footer_labels">
                    <span className="dashbox-footer-label">
                      Live properties:
                    </span>
                    <span className="dashbox-badge">
                      {stats?.propertiesActive ?? "-"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-4">
              <div className="dashbox">
                <div className="dashbox-heading">
                  <h3>Guardians</h3>
                </div>
                <div className="dashbox-body">
                  <Link
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      textTransform: "capitalize",
                    }}
                    to="/guardians"
                    className="btn btn-large btn-default btn-mint col-md-6"
                  >
                    List Guardians
                  </Link>
                  <Link
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      textTransform: "capitalize",
                    }}
                    to="/guardians/create"
                    className="btn btn-large btn-default btn-mint col-md-6"
                  >
                    <IconPlus /> Add new guardian
                  </Link>
                </div>
                <div className="dashbox-footer">
                  <div className="dashbox_footer_labels">
                    <span className="dashbox-footer-label">All guardians:</span>
                    <span className="dashbox-badge">
                      {stats?.guardians ?? "-"}
                    </span>
                  </div>
                  <br />
                  <div className="dashbox_footer_labels">
                    <span className="dashbox-footer-label">
                      Placed guardians:
                    </span>
                    <span className="dashbox-badge">
                      {stats?.placedGuardians ?? "-"}
                    </span>
                  </div>
                  {/* Registrations per trackable source link - both the name
                      and the count open the guardians list filtered to it */}
                  {stats?.guardiansBySource?.map((source) => (
                    <React.Fragment key={source.slug}>
                      <br />
                      <Link
                        to={`/guardians?source=${source.slug}`}
                        className="dashbox_footer_labels dashbox-source-link"
                        title={`View guardians from ${source.label}`}
                      >
                        <span className="dashbox-footer-label">
                          {source.label}:
                        </span>
                        <span className="dashbox-badge">{source.count}</span>
                      </Link>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="dashbox">
                <div className="dashbox-heading">
                  <h3>Tickets</h3>
                </div>
                <div className="dashbox-body">
                  <Link
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      textTransform: "capitalize",
                    }}
                    to="/tickets"
                    className="btn btn-large btn-default btn-mint col-md-6"
                  >
                    List tickets
                  </Link>
                  <Link
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      textTransform: "capitalize",
                    }}
                    to="/tickets/create"
                    className="btn btn-large btn-default btn-mint col-md-6"
                  >
                    <IconPlus /> Add new ticket
                  </Link>
                </div>
                <div className="dashbox-footer">
                  <div className="dashbox_footer_labels">
                    <span className="dashbox-footer-label">All tickets:</span>
                    <span className="dashbox-badge">
                      {stats?.tickets ?? "-"}
                    </span>
                  </div>
                  <br />
                  <div className="dashbox_footer_labels">
                    <span className="dashbox-footer-label">Live tickets:</span>
                    <span className="dashbox-badge">
                      {stats?.liveTickets ?? "-"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="dashbox">
                <div className="dashbox-heading">
                  <h3>Contractors</h3>
                </div>
                <div className="dashbox-body">
                  <Link
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      textTransform: "capitalize",
                    }}
                    to="/contractors"
                    className="btn btn-large btn-default btn-mint col-md-6"
                  >
                    List contractors
                  </Link>
                  <Link
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      textTransform: "capitalize",
                    }}
                    to="/contractors/create"
                    className="btn btn-large btn-default btn-mint col-md-6"
                  >
                    Add new contractor <IconPlus />
                  </Link>
                  <Link
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      textTransform: "capitalize",
                    }}
                    to="/services"
                    className="btn btn-large btn-default btn-mint col-md-12"
                  >
                    <IconEdit /> Manage contractors services
                  </Link>
                </div>
                <div className="dashbox-footer">
                  <div className="dashbox_footer_labels">
                    <span className="dashbox-footer-label">
                      All contractors:
                    </span>
                    <span className="dashbox-badge">
                      {stats?.contractors ?? "-"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {user?.role === "SYSTEM-ADMINISTRATOR" && (
            <div className="row">
              <div className="col-md-12">
                <div className="dashbox">
                  <div className="dashbox-heading-gold">
                    <h3>CRM components</h3>
                  </div>
                  <div className="dashbox-body">
                    <Link
                      to="/roles"
                      className="btn btn-large btn-default btn-mint col-md-3"
                    >
                      Roles
                    </Link>
                    <Link
                      to="/permissions"
                      className="btn btn-large btn-default btn-mint col-md-3"
                    >
                      Permissions
                    </Link>
                    <Link
                      to="/roles_permissions"
                      className="btn btn-large btn-default btn-mint col-md-3"
                    >
                      Roles permissions
                    </Link>
                    <Link
                      to="/settings"
                      className="btn btn-large btn-default btn-mint col-md-3"
                    >
                      Settings
                    </Link>
                  </div>
                  <div className="dashbox-footer">
                    <span className="dashbox-footer-label">
                      This section is visible only for System Administrator
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

    </div>
  );
};

export default Dashboard;
